import {
  DatabaseAuto,
  IauditLog,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IcommerceUser,
  IdataArrayResponse,
  IdeleteOneResponse,
  IfilterProps
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

/**
 * Type alias for audit log properties with specific generic types
 * Omits 'collectionName' from IauditLog as it's handled by the base class
 */
type TauditLogProps = Omit<IauditLog<string, IcommerceUser>, 'collectionName'>;

/**
 * Represents an audit log entry in the commerce system.
 * This class provides methods to track and manage user activities,
 * system events, and other auditable actions.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @template TauditLogProps - The type definition for audit log properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link AuditLog} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const auditLog = await AuditLog.getAll();
 *
 * const auditLog = await AuditLog.filterAll({
 *
 * });
 *
 * const auditLog = await AuditLog.bulkDelete({
 *
 * });
 *
 * const auditLog = await AuditLog.getOne('resourceId');
 *
 * const auditLog = await AuditLog.remove();
 *
 * ```
 */
export class AuditLog extends DatabaseAuto {
  /** Base API endpoint for audit log operations */
  private static readonly baseUrl = '/audit-log';

  /** ID of the parent document/entity this log entry is associated with */
  readonly parent: TauditLogProps['parent'];

  /** Information about the authenticated user who performed the action */
  readonly authUser: TauditLogProps['authUser'];

  /** Commerce user information associated with the action */
  readonly commerceUser: TauditLogProps['commerceUser'];

  /** Type of action that was performed (e.g., 'create', 'update', 'delete') */
  readonly action: TauditLogProps['action'];

  /** IP address from where the action was performed (optional) */
  readonly ip?: TauditLogProps['ip'];

  /** Device information of the user (optional) */
  readonly deviceInfo?: TauditLogProps['deviceInfo'];

  /** Additional information about the action */
  readonly info: TauditLogProps['info'];

  /** Company associated with the action (optional) */
  readonly company?: TauditLogProps['company'];

  /**
   * Creates a new instance of AuditLog.
   * @param {TauditLogProps} data - The audit log data
   */
  constructor(data: TauditLogProps) {
    super(data);

    this.parent = data.parent;
    this.action = data.action;
    this.authUser = data.authUser;
    this.commerceUser = data.commerceUser;
    this.ip = data.ip;
    this.deviceInfo = data.deviceInfo;
    this.info = data.info;
    this.company = data.company;
  }

  /**
   * Retrieves audit log entries filtered by parent ID with pagination support.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @param {string} parent - The parent ID to filter audit logs by
   * @returns {Promise<{ count: number; dataArr: AuditLog[] }>}
   * An object containing the total count and an array of audit log entries
   * @throws {Error} If there's an error applying the filter or fetching the logs
   */
  static async filterActivityByParent(
    filter: IfilterProps,
    parent: string
  ) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TauditLogProps>>(
        `${AuditLog.baseUrl}/filter-activity-by-parent/${parent}`,
        filter
      );
    const activitys = await lastValueFrom(observer$);

    return {
      count: activitys.count,
      dataArr: activitys.data.map(val => new AuditLog(val))
    };
  }

  /**
   * Retrieves audit log entries for a specific user with pagination support.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @param {string} userId - The ID of the user to filter audit logs by
   * @returns {Promise<{ count: number; dataArr: AuditLog[] }>}
   * An object containing the total count and an array of audit log entries
   * @throws {Error} If there's an error applying the filter or fetching the logs
   */
  static async filterActivityByUser(
    filter: IfilterProps,
    userId: string
  ) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TauditLogProps>>(
        `${AuditLog.baseUrl}/filter-activity-by-user/${userId}`,
        filter
      );
    const activitys = await lastValueFrom(observer$);

    return {
      count: activitys.count,
      dataArr: activitys.data.map(val => new AuditLog(val))
    };
  }

  /**
   * Deletes multiple audit log entries based on the provided criteria.
   * @param {IbulkDelete} payload - The criteria for bulk deletion
   * @returns {Promise<IbulkDeleteSuccessResponse>}
   * The result of the bulk deletion operation
   * @throws {Error} If the bulk deletion fails
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(
        `${AuditLog.baseUrl}/bulk-delete`,
        payload
      );

    return lastValueFrom(observer$);
  }

  /**
   * Deletes the current audit log entry.
   * @returns {Promise<IdeleteOneResponse | null>}
   * The result of the deletion operation
   * @throws {Error} If the deletion fails or the log entry doesn't exist
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${AuditLog.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
